import React from "react";
import Container from "react-bootstrap/Container";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import {
  SchoolStudent,
  LessonAssignment,
  TeacherLessonAssignments,
} from "../../common/api";
import { Lesson } from "../../common/api";
import { Col, Row, Stack } from "react-bootstrap";
import { LeftCornerDecoration, LessonItemBody } from "../LessonItemBody";
import { Account } from "../../common/context/AccountContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faArrowDown,
  faArrowUp,
} from "@fortawesome/pro-solid-svg-icons";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { ApiContext } from "../../common/context/ApiContext";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { TabEnum } from "../../common/context/TabsContext";
import { useHistory } from "react-router-dom";
import { packLessonIdAndCustomFlashpackId } from "../../common/utils";
import {
  COLOR_TEXT_PRIMARY,
  COLOR_GRAY_INACTIVE,
  COLOR_GRAY_MUTED,
  COLOR_RED,
} from "../../common/colors";
import { AppFlowActionButton } from "../../common/CommonStyles";

const Subhead = styled.span`
  color: ${COLOR_GRAY_MUTED};
`;


const ArrowIcon = styled(FontAwesomeIcon)`
  color: ${(p) => (p.disabled ? COLOR_GRAY_INACTIVE : COLOR_TEXT_PRIMARY)};
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
`;

const labelForStudent = (s: Readonly<SchoolStudent>) =>
  s.roster_first_name + " " + s.roster_last_name;

export const Confirmation = ({
  account,
  selectedStudents,
  selectedLessons,
  setSelectedLessons,
  selectedLessonsSortOrder,
  setSelectedLessonsSortOrder,
}: {
  account: Account;
  selectedStudents: ReadonlyMap<number, Readonly<SchoolStudent>>;
  selectedLessons: ReadonlyMap<string, Lesson>; // "[lesson_id]_[custom_flashpack_id?]"
  setSelectedLessons: React.Dispatch<
    React.SetStateAction<ReadonlyMap<string, Lesson>> // "[lesson_id]_[custom_flashpack_id?]"
  >;
  selectedLessonsSortOrder: Array<string>; // "[lesson_id]_[custom_flashpack_id?]"
  setSelectedLessonsSortOrder: React.Dispatch<
    React.SetStateAction<Array<string>> // "[lesson_id]_[custom_flashpack_id?]"
  >;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { createLessonAssignments } = React.useContext(ApiContext);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = React.useState(false);
  const sortedStudentNames = Array.from(selectedStudents.values())
    .map((s) => labelForStudent(s))
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

  // Keep the sort order in sync with selected lessons.
  // Use functional state updates to break dependence on the state
  // that we are changing to avoid forever looping rerenders.
  React.useEffect(() => {
    setSelectedLessonsSortOrder((prevSortOrder: Array<string>) => {
      const currentLessonIdAndCustomFlashpackId = Array.from(
        selectedLessons.keys()
      );
      // Filter out removed LessonIdAndCustomFlashpackIds from both prevOrder and current state
      const cleanedOrder = prevSortOrder.filter(
        (lessonIdAndCustomFlashpackId: string) =>
          selectedLessons.has(lessonIdAndCustomFlashpackId)
      );

      // Find new LessonIdAndCustomFlashpackIds not in previous sort order
      const newLessonIdAndCustomFlashpackId =
        currentLessonIdAndCustomFlashpackId.filter(
          (lessonIdAndCustomFlashpackId) =>
            !prevSortOrder.includes(lessonIdAndCustomFlashpackId)
        );

      return [...cleanedOrder, ...newLessonIdAndCustomFlashpackId];
    });
  }, [selectedLessons, setSelectedLessonsSortOrder]);

  const callCreateLessonAssignments = React.useCallback(async () => {
    startLoading();
    const profile_ids = Array.from(selectedStudents.values()).map((s) => s.id);
    const assignments: LessonAssignment[] = selectedLessonsSortOrder
      .map((lessonIdAndCustomFlashpackId) =>
        selectedLessons.get(lessonIdAndCustomFlashpackId)
      )
      // Type-guard to remove undefined values
      .filter((l): l is Lesson => !!l)
      .map((lesson) => ({
        lesson_id: lesson.id,
        custom_flashpack_id: lesson.custom_flashpack_id,
      }));
    const data: TeacherLessonAssignments = { assignments, profile_ids };
    await createLessonAssignments(data);
    stopLoading();
    setIsSuccessDialogOpen(true);
  }, [
    startLoading,
    stopLoading,
    selectedStudents,
    selectedLessons,
    selectedLessonsSortOrder,
    setIsSuccessDialogOpen,
    createLessonAssignments,
  ]);

  const unselectLesson = React.useCallback(
    (newLessonIdAndCustomFlashpackId: string) => {
      const newSelectedLessons = new Map(selectedLessons);
      newSelectedLessons.delete(newLessonIdAndCustomFlashpackId);
      setSelectedLessons(newSelectedLessons);
    },
    [selectedLessons, setSelectedLessons]
  );

  const moveLessonUpOrDown = React.useCallback(
    (fromIndex, up: boolean) => {
      const offset = up ? -1 : 1;
      const newOrder = [...selectedLessonsSortOrder];
      newOrder[fromIndex + offset] = selectedLessonsSortOrder[fromIndex];
      newOrder[fromIndex] = selectedLessonsSortOrder[fromIndex + offset];
      setSelectedLessonsSortOrder(newOrder);
    },
    [selectedLessonsSortOrder, setSelectedLessonsSortOrder]
  );

  const onSuccessDialogClosed = () => {
    history.push(TabEnum.MyClass);
  };

  const canContinue = selectedLessons.size > 0;

  return (
    <div>
      <Container className="my-4" style={{ maxWidth: "800px" }}>
        <Row className="border-top my-3 justify-content-center">
          <Col className="mt-2" xs="auto">
            <Subhead>{t("assignments_confirm_students")}</Subhead>
          </Col>
        </Row>
        <Row className="my-3 justify-content-center">
          <Col xs="auto">
            <span>{sortedStudentNames}</span>
          </Col>
        </Row>
        <Row className="border-top my-3 justify-content-center">
          <Col className="mt-2" xs="auto">
            <Subhead>{t("assignments_confirm_lessons")}</Subhead>
          </Col>
        </Row>
        {selectedLessonsSortOrder
          .map((id) => selectedLessons.get(id))
          // Type-guard to remove undefined values
          .filter((l): l is Lesson => !!l)
          .map((lesson, index) => (
            <Row
              key={`${lesson.id}-${lesson.custom_flashpack_id}`}
              className="my-3"
            >
              <Stack direction="horizontal">
                <div>
                  <FontAwesomeIcon
                    onClick={() =>
                      unselectLesson(
                        packLessonIdAndCustomFlashpackId(
                          lesson.id,
                          lesson.custom_flashpack_id
                        )
                      )
                    }
                    size="xl"
                    icon={faCircleMinus}
                    style={{ marginRight: "10px", color: COLOR_RED }}
                  />
                </div>
                <LessonItemBody
                  lesson={lesson}
                  account={account}
                  className="w-100"
                  leftCornerDecoration={LeftCornerDecoration.STANDARD}
                />
                <div style={{ marginLeft: "10px" }}>
                  <Stack>
                    <ArrowIcon
                      disabled={index < 1}
                      onClick={() => {
                        if (index > 0) {
                          moveLessonUpOrDown(index, true);
                        }
                      }}
                      size="xl"
                      icon={faArrowUp}
                      style={{ marginBottom: "0.5em" }}
                    />
                    <ArrowIcon
                      disabled={index >= selectedLessons.size - 1}
                      onClick={() => {
                        if (index < selectedLessons.size - 1) {
                          moveLessonUpOrDown(index, false);
                        }
                      }}
                      size="xl"
                      icon={faArrowDown}
                    />
                  </Stack>
                </div>
              </Stack>
            </Row>
          ))}
        <Row>
          <Col xs={12} md={{ span: 6, offset: 3 }}>
            <AppFlowActionButton
              disabled={!canContinue}
              isEnabled={canContinue}
              onClick={() => callCreateLessonAssignments()}
            >
              {selectedLessons.size === 1
                ? t("assignments_assign_one_lesson")
                : t("assignments_assign_n_lessons", {
                    count: selectedLessons.size,
                  })}
            </AppFlowActionButton>
          </Col>
        </Row>
      </Container>
      {/* Success dialog */}
      <Dialog
        open={isSuccessDialogOpen}
        onClose={onSuccessDialogClosed}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
      >
        <DialogTitle id="success-dialog-title">
          {t("generic_success")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="success-dialog-description">
            {t("assignments_success_description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onSuccessDialogClosed} autoFocus>
            {t("generic_ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
